import { useEffect, useMemo, useRef, useState } from "react";
import "./MathQuizApp.css";
import type { MathOperation, MathSettings } from "./MathMenu";
import { AudioManager } from "../audio/AudioManager";
import { MusicController } from "../audio/MusicController";

type InputMode = "choices" | "input";

interface MathQuestion {
  text: string;
  answer: number;
  choices: number[] | null;
  inputMode: InputMode;
  operation: MathOperation;
  level: number;
}

interface MathQuizAppProps {
  settings: MathSettings;
  onBack: () => void;
}

const OP_SYMBOL: Record<MathOperation, string> = {
  add: "+",
  sub: "−",
  mul: "×",
  div: "÷",
};

const OP_LABEL: Record<MathOperation, string> = {
  add: "Dodawanie",
  sub: "Odejmowanie",
  mul: "Mnożenie",
  div: "Dzielenie",
};

const ADD_MAX = [5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
const SUB_MAX = ADD_MAX;
const MUL_MAX = [5, 10, 12, 15, 20, 25, 30, 50, 100, 200];
const DIV_DIVISOR_MAX = [5, 10, 12, 15, 20, 25, 30, 40, 50, 60];
const DIV_QUOTIENT_MAX = [5, 10, 12, 15, 20, 25, 30, 40, 50, 80];

const randomInt = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const pickInputMode = (level: number): InputMode => {
  if (level <= 6) {
    return "choices";
  }
  if (level >= 10) {
    return "input";
  }
  return Math.random() < 0.5 ? "choices" : "input";
};

const buildChoices = (answer: number, level: number): number[] => {
  const choices = new Set<number>([answer]);
  const spread = Math.max(2, Math.round(Math.max(6, answer * 0.2)));
  while (choices.size < 3) {
    const delta = randomInt(1, spread + level * 2);
    const candidate = Math.random() < 0.5 ? answer - delta : answer + delta;
    if (candidate >= 0) {
      choices.add(candidate);
    }
  }
  return Array.from(choices).sort(() => Math.random() - 0.5);
};

const generateQuestion = (operation: MathOperation, level: number): MathQuestion => {
  let a = 0;
  let b = 0;
  let answer = 0;
  if (operation === "add") {
    const max = ADD_MAX[level - 1];
    a = randomInt(0, max);
    b = randomInt(0, max);
    answer = a + b;
  } else if (operation === "sub") {
    const max = SUB_MAX[level - 1];
    a = randomInt(0, max);
    b = randomInt(0, a);
    answer = a - b;
  } else if (operation === "mul") {
    const max = MUL_MAX[level - 1];
    a = randomInt(0, max);
    b = randomInt(0, max);
    answer = a * b;
  } else {
    const divisorMax = DIV_DIVISOR_MAX[level - 1];
    const quotientMax = DIV_QUOTIENT_MAX[level - 1];
    b = randomInt(1, divisorMax);
    const quotient = randomInt(1, quotientMax);
    a = b * quotient;
    answer = quotient;
  }

  const inputMode = pickInputMode(level);
  const choices = inputMode === "choices" ? buildChoices(answer, level) : null;
  return {
    text: `${a} ${OP_SYMBOL[operation]} ${b} = ?`,
    answer,
    choices,
    inputMode,
    operation,
    level,
  };
};

export function MathQuizApp({ settings, onBack }: MathQuizAppProps) {
  const audioRef = useRef<AudioManager | null>(null);
  const musicRef = useRef<MusicController | null>(null);
  const musicPrimedRef = useRef(false);
  if (!audioRef.current) {
    audioRef.current = new AudioManager({
      successUrl: `${process.env.PUBLIC_URL}/music/success.wav`,
      failUrl: `${process.env.PUBLIC_URL}/music/fail.wav`,
      successVolume: 0.8,
      failVolume: 0.8,
    });
  }
  if (!musicRef.current) {
    musicRef.current = new MusicController({
      playlist: [
        `${process.env.PUBLIC_URL}/music/bg_000.mp3`,
        `${process.env.PUBLIC_URL}/music/bg_001.mp3`,
      ],
      volume: 0.4,
    });
  }

  const enabledOperations = useMemo(() => {
    return Object.entries(settings.operations)
      .filter(([, value]) => value.enabled)
      .map(([key]) => key as MathOperation);
  }, [settings]);

  const [question, setQuestion] = useState<MathQuestion | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [lastResult, setLastResult] = useState<"correct" | "incorrect" | null>(
    null
  );
  const [userAnswer, setUserAnswer] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const timerRef = useRef<number | null>(null);

  const primeMusic = () => {
    if (musicPrimedRef.current) {
      return;
    }
    musicPrimedRef.current = true;
    musicRef.current?.start();
  };

  const nextQuestion = () => {
    if (enabledOperations.length === 0) {
      setQuestion(null);
      return;
    }
    const op =
      enabledOperations[randomInt(0, Math.max(0, enabledOperations.length - 1))];
    const level = settings.operations[op].level;
    setQuestion(generateQuestion(op, level));
    setInputValue("");
    setLastResult(null);
    setUserAnswer(null);
    setLocked(false);
  };

  useEffect(() => {
    nextQuestion();
    const onVisibilityChange = () => {
      if (document.hidden) {
        musicRef.current?.pause();
        return;
      }
      if (musicPrimedRef.current) {
        musicRef.current?.resume();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      musicRef.current?.stop();
    };
  }, [enabledOperations.length]);

  useEffect(() => {
    if (!lastResult) {
      return;
    }
    timerRef.current = window.setTimeout(() => {
      nextQuestion();
    }, 2000);
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [lastResult]);

  const handleAnswer = (value: number) => {
    if (!question || locked) {
      return;
    }
    primeMusic();
    const correct = value === question.answer;
    setUserAnswer(value);
    setLastResult(correct ? "correct" : "incorrect");
    setLocked(true);
    if (correct) {
      audioRef.current?.playSuccess();
    } else {
      audioRef.current?.playFail();
    }
    setScore((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));
  };

  const handleSubmit = () => {
    if (!question || locked) {
      return;
    }
    const value = Number(inputValue);
    if (!Number.isFinite(value)) {
      return;
    }
    primeMusic();
    handleAnswer(value);
  };

  return (
    <div className="MathQuizApp">
      <div className="MathQuizCard">
        <div className="MathQuizHeader">
          <div>
            <div className="MathQuizTitle">Matematyka</div>
            {question && (
              <div className="MathQuizMeta">
                {OP_LABEL[question.operation]} • Poziom {question.level}
              </div>
            )}
          </div>
          <button className="MathQuizBack" type="button" onClick={onBack}>
            Menu
          </button>
        </div>
        <div className="MathQuizQuestion">
          {question?.text ?? "Brak zadań"}
        </div>

        {question?.inputMode === "choices" && (
          <div className="MathQuizChoices">
            {question.choices?.map((choice) => (
              <button
                key={choice}
                className={`MathQuizChoice ${
                  locked && choice === question.answer ? "is-correct" : ""
                } ${locked && choice === userAnswer && choice !== question.answer ? "is-incorrect" : ""}`}
                type="button"
                disabled={locked}
                onClick={() => handleAnswer(choice)}
              >
                {choice}
              </button>
            ))}
          </div>
        )}

        {question?.inputMode === "input" && (
          <div className="MathQuizInput">
            <input
              type="number"
              inputMode="numeric"
              value={inputValue}
              disabled={locked}
              onChange={(event) => setInputValue(event.target.value)}
              onFocus={primeMusic}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSubmit();
                }
              }}
              placeholder="Wpisz wynik"
            />
            <button
              type="button"
              className="MathQuizSubmit"
              disabled={locked || inputValue.trim() === ""}
              onClick={handleSubmit}
            >
              Sprawdź
            </button>
          </div>
        )}

        <div className="MathQuizScore">
          Wynik: {score.correct}/{score.total}
        </div>
      </div>
    </div>
  );
}
